import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FiAlertTriangle, FiLock, FiRefreshCw } from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import AdminTopBar from './admin/AdminTopBar';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useThemeClasses } from '../hooks/useThemeClasses';

const AdminLayout = ({ children, requiredPermission = null }) => {
  const tc = useThemeClasses();
  const {
    canAccessABM,
    canManageUsers,
    canManageCompanies,
    canManagePlans,
    canManageSystem,
    getPlanName,
    loading,
    error,
  } = useAdminPermissions();

  const pageBg = tc.modoOscuro ? 'min-h-screen bg-black text-white' : 'min-h-screen bg-[#f5f5f7] text-gray-900';

  const mobileTabs = [
    { to: '/admin/dashboard', label: 'Inicio', show: canAccessABM() },
    { to: '/admin/users', label: 'Usuarios', show: canManageUsers() },
    { to: '/admin/companies', label: 'Empresas', show: canManageCompanies() },
    { to: '/admin/plans', label: 'Planes', show: canManagePlans() },
    { to: '/admin/beta', label: 'Beta', show: canManageSystem() },
    { to: '/admin/system', label: 'Sistema', show: canManageSystem() },
    { to: '/admin/fiscal', label: 'Fiscal', show: canManageSystem() },
  ].filter((tab) => tab.show);

  if (loading) {
    return (
      <div className={`${pageBg} flex items-center justify-center px-4`}>
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-gray-300 border-t-[#1F80FF] rounded-full animate-spin mx-auto mb-3" />
          <p className={`text-sm font-medium ${tc.textSec}`}>Verificando permisos…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${pageBg} flex items-center justify-center px-4`}>
        <div className={`rounded-2xl p-6 max-w-md w-full border text-center ${tc.box}`}>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${tc.modoOscuro ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-600'}`}>
            <FiAlertTriangle className="w-5 h-5" />
          </div>
          <h3 className={`text-base font-semibold mb-1 ${tc.textPri}`}>No se pudieron cargar los permisos</h3>
          <p className={`text-sm mb-4 ${tc.textSec}`}>{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}
          >
            <FiRefreshCw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!canAccessABM()) {
    return (
      <div className={`${pageBg} flex items-center justify-center px-4`}>
        <div className={`rounded-2xl p-6 max-w-md w-full border text-center ${tc.box}`}>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${tc.modoOscuro ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
            <FiLock className="w-5 h-5" />
          </div>
          <h3 className={`text-base font-semibold mb-1 ${tc.textPri}`}>Acceso restringido</h3>
          <p className={`text-sm mb-4 ${tc.textSec}`}>
            Tu plan actual ({getPlanName()}) no incluye el panel de administración.
          </p>
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={() => window.history.back()}
              className={`px-4 py-2 rounded-lg border text-sm ${tc.btnOutline}`}
            >
              Volver
            </button>
            <Link to="/planes" className={`px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}>
              Ver planes
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (requiredPermission && !requiredPermission()) {
    return (
      <div className={`${pageBg} flex items-center justify-center px-4`}>
        <div className={`rounded-2xl p-6 max-w-md w-full border text-center ${tc.box}`}>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${tc.modoOscuro ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
            <FiLock className="w-5 h-5" />
          </div>
          <h3 className={`text-base font-semibold mb-1 ${tc.textPri}`}>Permiso insuficiente</h3>
          <p className={`text-sm mb-4 ${tc.textSec}`}>
            No tenés acceso a esta sección del panel.
          </p>
          <button
            type="button"
            onClick={() => window.history.back()}
            className={`px-4 py-2 rounded-lg text-sm ${tc.btnPrimary}`}
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-1 items-stretch w-full max-w-[100vw] min-h-[calc(100dvh-5.25rem)] overflow-x-hidden ${
        tc.modoOscuro ? 'bg-black text-white' : 'bg-[#f5f5f7] text-gray-900'
      }`}
    >
      <div className="hidden lg:flex shrink-0 self-stretch">
        <AdminSidebar />
      </div>
      <div className="flex-1 flex flex-col min-w-0 w-full max-w-full overflow-x-hidden">
        <div className="px-4 sm:px-6 pt-3 lg:pt-4 pb-2 lg:hidden max-w-full">
          <div className={`inline-flex w-full overflow-x-auto no-scrollbar rounded-full p-1 ${tc.modoOscuro ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}>
            {mobileTabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-[11px] font-medium text-center whitespace-nowrap flex-1 ${
                    isActive
                      ? 'bg-[#1F80FF] text-white'
                      : tc.modoOscuro
                        ? 'text-gray-300'
                        : 'text-gray-600'
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </div>
        </div>
        <AdminTopBar />
        <main className="flex-1 min-w-0 w-full max-w-full overflow-x-hidden px-4 sm:px-6 pb-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
